"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { logoutFailureMessage, requestLogout } from "../../lib/ui/logout";

export default function AuthenticatedAccountMenu({ kind }: { kind: "customer" | "staff" }) {
  const router = useRouter();
  const pathname = usePathname();
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [openPathname, setOpenPathname] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const isCustomer = kind === "customer";
  const open = openPathname === pathname;
  const closeMenu = useCallback(() => setOpenPathname(null), []);

  useEffect(() => {
    if (!open) return;
    function closeOutside(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) closeMenu();
    }
    function closeWithEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeMenu();
      triggerRef.current?.focus();
    }
    document.addEventListener("pointerdown", closeOutside, true);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [closeMenu, open]);

  async function logout() {
    setSubmitting(true);
    setLogoutError(null);
    try {
      await requestLogout(kind);
      closeMenu();
      router.push(isCustomer ? "/login" : "/admin/login");
      router.refresh();
    } catch {
      setLogoutError(logoutFailureMessage);
    } finally {
      setSubmitting(false);
    }
  }

  const profileHref = isCustomer ? "/portal/account" : "/admin/profile";
  const securityHref = isCustomer ? "/portal/account#account-security" : "/admin/security";

  return (
    <div ref={containerRef} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) closeMenu(); }} className="relative">
      <button ref={triggerRef} type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={menuId} onClick={() => setOpenPathname(open ? null : pathname)} className="inline-flex min-h-11 items-center rounded-md border border-[var(--vintera-border)] bg-white px-3.5 py-2 text-[var(--vintera-navy)] transition hover:bg-[var(--vintera-sage-soft)]">
        Account <span aria-hidden="true" className={`ml-1 transition ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && <div id={menuId} role="menu" className="absolute right-0 z-50 mt-2 grid min-w-52 overflow-hidden rounded-lg border border-[var(--vintera-border)] bg-white p-1.5 text-sm shadow-xl">
        <Link role="menuitem" href={profileHref} onClick={closeMenu} className="rounded-md px-3 py-2.5 text-[var(--vintera-navy)] hover:bg-[var(--vintera-sage-soft)]">My Profile / Account</Link>
        <Link role="menuitem" href={securityHref} onClick={closeMenu} className="rounded-md px-3 py-2.5 text-[var(--vintera-navy)] hover:bg-[var(--vintera-sage-soft)]">Security</Link>
        <button type="button" disabled={submitting} onClick={logout} className="rounded-md px-3 py-2.5 text-left font-semibold text-[var(--vintera-navy)] hover:bg-[var(--vintera-sage-soft)] disabled:opacity-60">
          {submitting ? "Logging out…" : "Log Out"}
        </button>
      </div>}
      {logoutError && <p role="alert" className="mt-2 max-w-xs text-sm text-red-800">{logoutError}</p>}
    </div>
  );
}
